import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";

export function AddUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dealerships, setDealerships] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user", dealership_id: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (open) api.get("/dealerships").then(({ data }) => setDealerships(data)).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error("Name, email and a password (6+ chars) are required");
      return;
    }
    if (["user", "technician"].includes(form.role) && !form.dealership_id) {
      toast.error("Please select a dealership for this user");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", { ...form, dealership_id: ["user", "technician"].includes(form.role) ? form.dealership_id : null });
      toast.success(`User ${form.email} invited`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "user", dealership_id: "" });
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-user-button"><UserPlus className="h-4 w-4" /> Add User</Button>
      </DialogTrigger>
      <DialogContent className="bg-card" data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">Invite New User</DialogTitle>
          <DialogDescription>Create an account. The user signs in with the email & password you set here.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Full Name</Label>
            <Input data-testid="new-user-name" className="bg-secondary/40" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input type="email" data-testid="new-user-email" className="bg-secondary/40" value={form.email} onChange={set("email")} />
          </div>
          <div>
            <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Temporary Password</Label>
            <Input type="text" data-testid="new-user-password" className="bg-secondary/40" value={form.password} onChange={set("password")} placeholder="min 6 characters" />
          </div>
          <div>
            <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Role</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger data-testid="new-user-role" className="bg-secondary/40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Warranty Administrator</SelectItem>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="admin">Owner / Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {["user", "technician"].includes(form.role) && (
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Dealership</Label>
              <Select value={form.dealership_id} onValueChange={set("dealership_id")}>
                <SelectTrigger data-testid="new-user-dealership" className="bg-secondary/40"><SelectValue placeholder="Select dealership" /></SelectTrigger>
                <SelectContent>
                  {dealerships.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-user-submit">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
